<script setup>
import axios from "axios";
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";
import { useToast } from "vue-toastification";

const { plan, plansBenefits, userData } = defineProps(["plan", "plansBenefits", "userData"]);
const emit = defineEmits(["closeModal"]);

const { t, locale } = useI18n();
const router = useRouter();

const createSubscription = async (plan, provider) => {
  // Use current URL as cancel URL with a parameter to indicate cancelled payment
  const cancelUrl = `${window.location.href}${window.location.href.includes('?') ? '&' : '?'}payment_cancelled=true`;

  await axios
    .post("/api/user/subscription/create", {
      plan: plan,
      provider: provider,
      cancelUrl: cancelUrl,
    })
    .then((res) => {
      emit("closeModal");

      if (userData?.is_temporary) {
        router.push("/register");
      } else {
        window.location.replace(res.data.data.checkout_url);
      }
    })
    .catch((error) => {
      useToast().error(error.response.data.message);
    });
};

const priceSuffix = computed(() => {
  switch (plan?.type) {
    case "recurring":
      return t("billing.per_month");
    case "one_time_payment":
      return t("billing.one_time");
    default:
      return "";
  }
});

// Safety check for plan data
const isValidPlan = computed(() => {
  return plan &&
         plan.id &&
         plan.name &&
         plan.currency_symbol &&
         (plan.unit_price !== undefined && plan.unit_price !== null) &&
         (plan.price !== undefined && plan.price !== null);
});

// Format currency symbol - change PLN to zł
const formatCurrency = (currencySymbol) => {
  return currencySymbol === 'PLN' ? 'PLN' : currencySymbol || '';
};
</script>
<template>
  <div v-if="isValidPlan" class="plan-card" :class="{ 'highlighted': plan.is_default }" :key="plan.id">
    <!-- Popular Badge -->
    <div v-if="plan.is_default" class="popular-badge">
      <span>{{ locale === 'pl' ? 'Polecane' : 'Popular' }}</span>
    </div>

    <div class="plan-header">
      <h3 class="plan-title">{{ plan.name || '' }}</h3>
      <div class="plan-price">
        <div class="price-main">
          <span class="price-amount">{{ plan.unit_price || 0 }}</span>
          <span class="price-currency">{{ formatCurrency(plan.currency_symbol) }}</span>
        </div>
        <div class="price-period">/ {{ priceSuffix }}</div>
      </div>

      <!-- PROMO40 Banner -->
      <div class="promo-banner">
        <span class="promo-text">{{ t("header.promo_code_banner") }}</span>
      </div>
    </div>

    <div class="plan-features">
      <div class="features-list">
        <div class="feature" v-for="(feature, index) in plansBenefits" :key="index">
          <v-icon icon="mdi-check-circle" color="success" size="18" class="feature-icon" />
          <span class="feature-text">{{ feature }}</span>
        </div>
      </div>
    </div>

    <div class="plan-actions">
      <button class="btn-primary" @click="() => plan?.id && createSubscription(plan.id, 'stripe')">
        {{ t("billing.buy_now").toUpperCase() }}
      </button>

      <div class="discount-section">
        <div class="discount-label">
          <v-icon icon="mdi-star" color="warning" size="16" />
          <span>{{ t("billing.crypto_discount") }}</span>
        </div>
        <button class="btn-discount" @click="() => plan?.id && createSubscription(plan.id, 'vaiot')">
          {{ locale === 'pl' ? 'Wybieram' : 'Choose' }}
        </button>
      </div>
    </div>
  </div>
</template>
<style scoped lang="scss">
.plan-card {
  width: 260px;
  min-height: 460px;
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 16px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.06);
  padding: 24px 20px;
  position: relative;
  transition: all 0.3s ease;
  display: flex;
  flex-direction: column;

  &:hover {
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
    transform: translateY(-4px);
  }

  &.highlighted {
    border: 2px solid #0023ef;
    box-shadow: 0 8px 24px rgba(0, 35, 239, 0.15);

    &:hover {
      box-shadow: 0 12px 32px rgba(0, 35, 239, 0.25);
    }
  }

  @media (max-width: 1200px) {
    width: 300px;
    padding: 28px;
    min-height: 480px;
  }

  @media (max-width: 1100px) {
    width: 280px;
    padding: 24px;
    min-height: 470px;
  }

  @media (max-width: 1000px) {
    width: 270px;
    padding: 22px;
    min-height: 460px;
  }

  @media (max-width: 950px) {
    width: 100%;
    max-width: 500px;
    min-height: auto;
    margin-bottom: 20px;
    padding: 32px;
  }

  @media (max-width: 768px) {
    padding: 24px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  }
}

.popular-badge {
  position: absolute;
  top: -1px;
  left: -1px;
  background: #0023ef;
  color: white;
  padding: 8px 16px;
  border-radius: 16px 0 16px 0;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  z-index: 3;
  transform: none;
}

.plan-header {
  text-align: center;
  margin-bottom: 32px;
  margin-top: 12px;
  padding-bottom: 24px;
  border-bottom: 1px solid #f0f0f0;
}

.plan-title {
  font-size: 1.25rem;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0 0 12px 0;
  font-family: 'Archivo', sans-serif;
}

.plan-price {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  margin-bottom: 8px;

  .price-main {
    display: flex;
    align-items: baseline;
    gap: 4px;
    margin-bottom: 4px;
  }

  .price-amount {
    font-size: 2rem;
    font-weight: 800;
    color: #1a1a1a;
  }

  .price-currency {
    font-size: 1rem;
    font-weight: 600;
    color: #666;
    text-transform: uppercase;
  }

  .price-period {
    font-size: 0.875rem;
    color: #999;
    text-align: center;
  }
}

.promo-banner {
  background: #d3f073;
  border: 1px solid #c9e85a;
  border-radius: 8px;
  padding: 8px 12px;
  margin-top: 16px;

  .promo-text {
    font-size: 0.75rem;
    font-weight: 600;
    color: #1a1a1a;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
}


.plan-features {
  flex: 1;
  margin-bottom: 32px;
}

.features-list {
  .feature {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    margin-bottom: 12px;
    font-size: 0.875rem;
    color: #555;
    line-height: 1.5;

    &:last-child {
      margin-bottom: 0;
    }
  }

  .feature-icon {
    flex-shrink: 0;
    margin-top: 2px;
  }

  .feature-text {
    flex: 1;
  }
}

.plan-actions {
  margin-top: auto;
}

.btn-primary {
  width: 100%;
  background: #0023ef;
  color: white;
  border: none;
  border-radius: 8px;
  padding: 12px 14px;
  font-size: 0.875rem;
  font-weight: 600;
  font-family: 'Archivo', sans-serif;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-bottom: 12px;
  text-transform: uppercase;
  letter-spacing: 0.5px;

  &:hover:not(:disabled) {
    background: #0019cc;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 35, 239, 0.3);
  }

  &:active {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
}

.discount-section {
  border: 1px solid #d3f073;
  border-radius: 8px;
  padding: 12px;
  background: #f9fdf4;
}

.discount-label {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  font-size: 0.75rem;
  font-weight: 600;
  color: #1a1a1a;
  margin-bottom: 10px;
  white-space: nowrap;
}

.btn-discount {
  width: 100%;
  background: #d3f073;
  color: #1a1a1a;
  border: none;
  border-radius: 6px;
  padding: 10px 16px;
  font-size: 0.875rem;
  font-weight: 600;
  font-family: 'Archivo', sans-serif;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: #c9e85a;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(211, 240, 115, 0.3);
  }

  &:active {
    transform: translateY(0);
  }
}
</style>